import { Overlay, OverlayPositionBuilder, type OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	inject,
	OnDestroy,
	Output,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { SharedModule } from '@shared/modules/shared.module';
import { activeLocale } from 'app/core/utils/locale';

/** Same breakpoint as the rest of the responsive rules and _sheets.scss. */
const MOBILE_QUERY = '(max-width: 844px)';
const SHEET_OUT_MS = 240;

export interface PillFilterOption<T = string> {
	value: T;
	label: string;
	icon?: string;
}

@Component({
	selector: 'app-pill-filter',
	imports: [SharedModule],
	templateUrl: './pill-filter.component.html',
	styleUrl: './pill-filter.component.scss',
})
export class PillFilterComponent<T = string> implements OnDestroy {
	@Input() label = '';
	@Input() options: PillFilterOption<T>[] = [];
	/** 'daterange' opens a range calendar instead of the option list. */
	@Input() mode: 'select' | 'daterange' = 'select';
	@Input() value: T | null = null;
	/** false = no X on the chip (filters that always have a value, like the matrix section). */
	@Input() clearable = true;
	@Input() disabled = false;
	/**
	 * Splits the options into columns of 10 (`multi-column`). For long lists:
	 * the 28 sections of the permission matrix did not fit the screen height.
	 */
	@Input() panelClass = '';
	@Output() valueChange = new EventEmitter<T | null>();
	@Output() clear = new EventEmitter<void>();

	@ViewChild('panel', { static: true }) panelTpl!: TemplateRef<unknown>;
	@ViewChild('trigger', { static: true }) trigger!: ElementRef<HTMLElement>;

	private overlay = inject(Overlay);
	private positionBuilder = inject(OverlayPositionBuilder);
	private vcr = inject(ViewContainerRef);
	private translocoService = inject(TranslocoService);
	private overlayRef: OverlayRef | null = null;

	open = false;
	private closeTimer?: ReturnType<typeof setTimeout>;
	private closingRef: OverlayRef | null = null;

	get selectedOption(): PillFilterOption<T> | null {
		return this.options.find((o) => o.value === this.value) ?? null;
	}

	get hasValue(): boolean {
		// With half a range picked the chip does NOT count: only the full range
		// is emitted, so drawing it earlier showed a filter that had not been
		// applied. A single day is picked by clicking it twice.
		if (this.mode === 'daterange') return !!(this.rangeDates?.[0] && this.rangeDates?.[1]);
		// An empty value does NOT count even if there is an option with value: null (the
		// usual "All"): otherwise the filter started out marked without being touched.
		if (this.value === null || this.value === undefined || (this.value as unknown) === '') return false;
		return this.selectedOption !== null;
	}

	get rangeDates(): Date[] | null {
		return Array.isArray(this.value) ? (this.value as unknown as Date[]) : null;
	}

	get rangeLabel(): string {
		const [start, end] = this.rangeDates ?? [];
		const locale = activeLocale(this.translocoService);
		const fmt = (d: Date) => d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
		if (!start || !end) return '';
		// A one-day range reads better as a single date
		return start.getTime() === end.getTime() ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
	}

	/** The chip is only drawn (and emitted) with the full range. */
	onRangeChange(dates: Date[] | null): void {
		this.value = dates as unknown as T;
		if (dates?.[0] && dates?.[1]) {
			this.valueChange.emit(this.value);
			this.closePanel();
		}
	}

	togglePanel(): void {
		if (this.disabled) return;
		if (this.open) this.closePanel();
		else this.openPanel();
	}

	openPanel(): void {
		this.overlayRef?.dispose();
		const isSheet = window.matchMedia(MOBILE_QUERY).matches;

		// On mobile, a bottom sheet stuck to the bottom (like the other dropdowns); on
		// desktop, anchored to the pill.
		const positionStrategy = isSheet
			? this.overlay.position().global().bottom('0').left('0')
			: this.positionBuilder.flexibleConnectedTo(this.trigger).withPositions([
					{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
					{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
				]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			hasBackdrop: true,
			backdropClass: isSheet ? 'cdk-overlay-dark-backdrop' : 'cdk-overlay-transparent-backdrop',
			panelClass: isSheet ? 'pill-filter-sheet' : [],
			width: isSheet ? '100vw' : undefined,
			scrollStrategy: isSheet
				? this.overlay.scrollStrategies.block()
				: this.overlay.scrollStrategies.reposition(),
		});

		this.overlayRef.attach(new TemplatePortal(this.panelTpl, this.vcr));
		this.overlayRef.backdropClick().subscribe(() => this.closePanel());
		// Escape closes the panel and gives the focus back to the pill
		this.overlayRef.keydownEvents().subscribe((event) => {
			if (event.key !== 'Escape') return;
			event.preventDefault();
			this.closePanel();
			this.trigger.nativeElement.focus();
		});
		this.open = true;
	}

	closePanel(): void {
		const ref = this.overlayRef;
		this.overlayRef = null;
		this.open = false;
		if (!ref) return;
		if (!ref.overlayElement.classList.contains('pill-filter-sheet')) {
			ref.dispose();
			return;
		}
		// Sheet: slides down before unmounting (the animation is in the scss)
		ref.overlayElement.classList.add('closing');
		ref.backdropElement?.classList.remove('cdk-overlay-backdrop-showing');
		clearTimeout(this.closeTimer);
		this.closingRef?.dispose();
		this.closingRef = ref;
		this.closeTimer = setTimeout(() => {
			ref.dispose();
			this.closingRef = null;
		}, SHEET_OUT_MS);
	}

	selectOption(option: PillFilterOption<T>): void {
		this.value = option.value;
		this.valueChange.emit(option.value);
		this.closePanel();
	}

	clearValue(event: Event): void {
		event.stopPropagation();
		this.value = null;
		this.valueChange.emit(null);
		this.clear.emit();
	}

	ngOnDestroy(): void {
		clearTimeout(this.closeTimer);
		this.closingRef?.dispose();
		this.overlayRef?.dispose();
	}
}
